import { Component, computed, inject, input, linkedSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipDirective } from '@vydaps/gui-commons';
import { UseForTrainingCheckboxComponent } from '@ps-gui/claims-terminal-shared';
import { SharedModule } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ExtractionConfiguration } from '../../../models/domain-extraction-specifications';
import { ExtractedValuesByKey } from '../../../models/extracted-values';
import {
  fieldGroupSectionsByDomainAndDocumentClass,
  tableSectionsByDomainAndDocumentClass,
} from '../../../models/extraction-models/extraction-models';
import { CtBatchStoreService } from '../../../service/app-state/ct-batch-store.service';
import { PersistDataService } from '../../../service/app-state/persist-data.service';
import { WorkflowService } from '../../../service/app-state/workflow.service';
import { ExtractedLocationStoreService } from '../../../service/overlay/extracted-location-store.service';
import { DialogComponent } from '../dialog/dialog.component';
import { DynamicDialog } from '../dynamic-dialog';

@Component({
  selector: 'ct-extraction-completion-dialog',
  imports: [
    ConfirmDialogModule,
    SharedModule,
    FormsModule,
    UseForTrainingCheckboxComponent,
    TooltipDirective,
    DialogComponent,
  ],
  templateUrl: './document-completion-dialog.component.html',
})
export class DocumentCompletionDialogComponent implements DynamicDialog<{
  accept: boolean;
  useExtractedDataForTraining: boolean;
}> {
  protected readonly ref = inject(DynamicDialogRef);

  readonly documentId = input.required<string>();
  readonly documentClass = input.required<string>();
  readonly extractedRowCountByTableKey = input.required<Record<string, number>>();
  readonly extractedBatchFields = input.required<ExtractedValuesByKey>();

  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly workflowService = inject(WorkflowService);
  protected readonly persistDataService = inject(PersistDataService);
  protected readonly extractedLocationsStoreService = inject(ExtractedLocationStoreService);

  protected readonly batchFieldGroupModels = computed(() => {
    const sections = fieldGroupSectionsByDomainAndDocumentClass[
      this.ctBatchStoreService.ctBatchDomain
    ] as Record<string, ExtractionConfiguration>;
    return sections[this.documentClass()].extractionPages.flatMap((page) => page.sections);
  });

  protected readonly tableModels = computed(() => {
    const tableSections = tableSectionsByDomainAndDocumentClass[
      this.ctBatchStoreService.ctBatchDomain
    ] as Record<string, ExtractionConfiguration>;
    return tableSections[this.documentClass()].extractionPages
      .flatMap((page) => page.sections)
      .filter((section) => section.type === 'tableModel');
  });

  protected readonly useExtractedDataForTraining = linkedSignal(() =>
    this.persistDataService.shouldUseTrainingDataForDocument(this.documentId()),
  );

  onEditFieldPressed(fieldKey: string) {
    this.workflowService.navigateToViewForFieldKey(this.documentId(), fieldKey);
    this.ref.close();
  }
}
